<script setup lang="ts">
import {computed} from "vue";
import {Dayjs} from "dayjs";
import LegacyAccess from "@/core/interfaces/Access";
import {TimeRange, formatTime, jobRoleForRank, jobRoleOption, toMinutes} from "@/team/planning";
import {MemberDay, dayCoverage} from "@/planning/lib/weekPlanning";
import {ShieldAlert, ShieldCheck} from "@lucide/vue";

const DAY_START = 8 * 60;
const DAY_END = 20 * 60;

const {days, selectedDate, members, coverageMembers, resolveDay} = defineProps<{
    days: Dayjs[];
    selectedDate: Dayjs;
    /** Members shown as lanes (role filter applied), a subset of coverageMembers. */
    members: LegacyAccess[];
    /** Full team: the coverage badge and gaps must ignore the role filter. */
    coverageMembers: LegacyAccess[];
    resolveDay: (member: LegacyAccess, date: Dayjs) => MemberDay;
}>();

const emits = defineEmits<{
    (event: 'select-date', date: Dayjs): void;
    (event: 'edit-range', payload: { day: MemberDay; rangeIndex: number; proposed: { starts_at: string; ends_at: string } }): void;
}>();

const hours = Array.from({length: 13}, (_, index) => DAY_START + index * 60);

/** One resolution of the full team, shared by the lanes and the coverage. */
const resolvedAll = computed(() => coverageMembers.map(member => resolveDay(member, selectedDate)));

const resolved = computed(() => {
    const visibleIds = new Set(members.map(member => member.id));

    return resolvedAll.value.filter(day => visibleIds.has(day.member.id) && day.ranges.length);
});

const coverage = computed(() => dayCoverage(resolvedAll.value));

const top = (minute: number): number => (minute - DAY_START) / (DAY_END - DAY_START) * 100;

const clampMinute = (minute: number): number => Math.min(Math.max(minute, DAY_START), DAY_END);

/** Block geometry clamped to the 8h–20h window, kept minimally visible. */
const blockTop = (range: TimeRange): number =>
    Math.min(top(clampMinute(toMinutes(range.starts_at))), 98);

const blockHeight = (range: TimeRange): number =>
    Math.max(top(clampMinute(toMinutes(range.ends_at))) - top(clampMinute(toMinutes(range.starts_at))), 2);

/** Coverage gaps clipped to the displayed window. */
const visibleGaps = computed(() => coverage.value.gaps
    .map(gap => ({start: clampMinute(gap.start), end: clampMinute(gap.end)}))
    .filter(gap => gap.end > gap.start)
);

const laneWidth = computed(() => 100 / Math.max(resolved.value.length, 1));

const roleOf = (day: MemberDay) => jobRoleOption(jobRoleForRank(day.member.rankId));
</script>

<template>
  <div class="mb-3 flex max-w-xl gap-2">
    <button
      v-for="date in days"
      :key="date.valueOf()"
      class="h-8 flex-1 rounded-lg border text-xs font-semibold capitalize transition-colors"
      :class="date.isSame(selectedDate, 'day')
        ? 'border-primary bg-primary text-white'
        : 'border-gray-200 bg-white text-gray-600 hover:border-primary/40'"
      @click="emits('select-date', date)"
    >
      {{ date.format('ddd DD') }}
    </button>
  </div>

  <div class="mb-3 flex items-center gap-2.5">
    <div class="text-sm font-semibold capitalize">
      {{ selectedDate.format('dddd DD MMMM') }}
    </div>
    <span
      class="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold"
      :class="coverage.status === 'covered' ? 'bg-green-50 text-green-700'
        : coverage.status === 'fragile' ? 'bg-amber-50 text-amber-700'
          : coverage.status === 'uncovered' ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-400'"
    >
      <component :is="coverage.status === 'uncovered' ? ShieldAlert : ShieldCheck" class="size-3.5" />
      {{ coverage.status === 'covered' ? 'Couverture conforme'
        : coverage.status === 'fragile' ? 'Couverture fragile'
          : coverage.status === 'uncovered' ? 'Trous de couverture pharmacien' : 'Fermé' }}
    </span>
  </div>

  <div class="flex rounded-xl border border-gray-200 bg-white p-3.5 shadow-sm">
    <!-- Hour scale -->
    <div class="relative h-[600px] w-11 flex-none">
      <div
        v-for="hour in hours"
        :key="hour"
        class="absolute right-2 -translate-y-1/2 text-[9.5px] text-gray-400"
        :style="{top: `${top(hour)}%`}"
      >
        {{ hour / 60 }}h
      </div>
    </div>

    <!-- Lanes -->
    <div class="relative h-[600px] flex-1 border-l border-gray-200">
      <div
        v-for="hour in hours"
        :key="hour"
        class="absolute inset-x-0 h-px bg-gray-100"
        :style="{top: `${top(hour)}%`}"
      />

      <!-- Pharmacist coverage gaps -->
      <div
        v-for="(gap, index) in visibleGaps"
        :key="index"
        class="absolute inset-x-0 flex items-center justify-center gap-1.5 border-y border-dashed border-red-400 text-[11px] font-semibold text-red-700"
        :style="{
          top: `${top(gap.start)}%`,
          height: `${top(gap.end) - top(gap.start)}%`,
          backgroundImage: 'repeating-linear-gradient(45deg, rgba(235,94,87,.14) 0, rgba(235,94,87,.14) 8px, rgba(235,94,87,.22) 8px, rgba(235,94,87,.22) 16px)'
        }"
      >
        <ShieldAlert class="size-3.5" />
        Aucun pharmacien
      </div>

      <!-- Member blocks -->
      <template v-for="(day, laneIndex) in resolved" :key="day.member.id">
        <div
          v-for="(range, rangeIndex) in day.ranges"
          :key="rangeIndex"
          class="absolute cursor-pointer p-0.5"
          :style="{
            left: `${laneIndex * laneWidth}%`,
            width: `${laneWidth}%`,
            top: `${blockTop(range)}%`,
            height: `${blockHeight(range)}%`
          }"
          @click="emits('edit-range', {day, rangeIndex, proposed: range})"
        >
          <div
            class="h-full w-full overflow-hidden rounded-md border-l-[3px] px-1.5 py-1 transition-[filter] hover:brightness-95"
            :style="{
              backgroundColor: (roleOf(day)?.color ?? '#8b93a7') + '1f',
              borderLeftColor: roleOf(day)?.color ?? '#8b93a7'
            }"
          >
            <div class="truncate text-[10.5px] font-semibold" :style="{color: roleOf(day)?.color ?? '#6b7280'}">
              {{ day.member.user?.name }}
            </div>
            <div class="text-[9px] opacity-80" :style="{color: roleOf(day)?.color ?? '#6b7280'}">
              {{ formatTime(range.starts_at) }}–{{ formatTime(range.ends_at) }}
            </div>
            <div class="mt-px text-[8.5px] opacity-70" :style="{color: roleOf(day)?.color ?? '#6b7280'}">
              {{ day.member.rank?.title ?? roleOf(day)?.label ?? '' }}
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
